import { Loader2 } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/features/auth/AuthProvider";

import {
  fetchLegalDocument,
  hasAcceptedDisclaimer,
  recordDisclaimerAcceptance,
} from "./legalDocuments";
import { renderMarkdown } from "./renderMarkdown";

/**
 * SPEC-DOCCONSENT-3b — in-app disclaimer gate.
 *
 * Lives inside RequireAuth on the confirmed-user path. After login,
 * EVERY user must accept the current disclaimer (beta + liability)
 * exactly ONCE before they can use any part of the app. Until the
 * acceptance is recorded in `public.document_consents`, a blocking
 * modal covers the protected subtree on every route; once recorded,
 * the gate unmounts and never reappears unless `DISCLAIMER_VERSION` is
 * bumped (because `hasAcceptedDisclaimer` keys on the current version).
 *
 * KVKK CRITICAL invariants enforced here:
 *   • The agree checkbox defaults UNCHECKED.
 *   • The accept button is disabled until the checkbox is checked.
 *   • There is NO dismiss affordance: no close X, scrim-click is
 *     inert, ESC is inert (rolled as a plain div so Radix's
 *     onEscapeKeyDown / onPointerDownOutside never fire).
 *   • The gate fails CLOSED: any error reading existing acceptance
 *     keeps the gate up rather than letting the user past.
 */
export function DisclaimerGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  type Status = "pending" | "accepted" | "needs-decision";
  const [status, setStatus] = React.useState<Status>("pending");
  const [agreed, setAgreed] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [docContent, setDocContent] = React.useState<string | null>(null);
  const [docError, setDocError] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setStatus("pending");
    hasAcceptedDisclaimer().then((ok) => {
      if (cancelled) return;
      setStatus(ok ? "accepted" : "needs-decision");
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  React.useEffect(() => {
    if (status !== "needs-decision") return;
    let cancelled = false;
    setDocError(false);
    setDocContent(null);
    fetchLegalDocument("disclaimer").then((row) => {
      if (cancelled) return;
      if (!row) {
        setDocError(true);
        return;
      }
      setDocContent(row.content);
    });
    return () => {
      cancelled = true;
    };
  }, [status]);

  React.useEffect(() => {
    if (status !== "needs-decision") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [status]);

  const handleAccept = async () => {
    if (!agreed || submitting) return;
    setSubmitting(true);
    try {
      await recordDisclaimerAcceptance({
        fullName:
          (user?.user_metadata as { full_name?: string } | undefined)
            ?.full_name ?? "",
        email: user?.email ?? "",
      });
      setStatus("accepted");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Onay kaydedilemedi: ${message}`);
    } finally {
      // Finding E7 — always clear submitting (not only on error). On success
      // the gate normally renders children, but if user?.id changes during the
      // await the status effect reverts to needs-decision; leaving
      // submitting=true would strand the accept button disabled/spinning with
      // no retry but a full reload.
      setSubmitting(false);
    }
  };

  if (status === "pending") {
    return <div className="min-h-screen w-full bg-background" aria-hidden />;
  }
  if (status === "accepted") {
    return <>{children}</>;
  }

  return (
    <div
      data-testid="disclaimer-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="disclaimer-gate-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
    >
        <div className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-4 rounded-xl bg-popover p-6 text-popover-foreground shadow-xl ring-1 ring-foreground/10">
          <h2
            id="disclaimer-gate-title"
            className="text-lg font-semibold text-foreground"
          >
            Sorumluluk Reddi
          </h2>
          <div
            data-testid="disclaimer-content"
            className="min-h-32 max-h-[60vh] overflow-y-auto rounded-md border border-border px-3 py-2"
          >
            {docError ? (
              <p className="py-12 text-center text-destructive">
                Belge yüklenemedi. Lütfen daha sonra tekrar deneyin.
              </p>
            ) : docContent ? (
              renderMarkdown(docContent)
            ) : (
              <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Yükleniyor…
              </div>
            )}
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="disclaimer-agree"
              checked={agreed}
              onCheckedChange={(v) => setAgreed(v === true)}
              data-testid="disclaimer-agree-checkbox"
              className="mt-0.5"
            />
            <label
              htmlFor="disclaimer-agree"
              className="cursor-pointer text-sm leading-relaxed"
            >
              Okudum, anladım ve onaylıyorum.
            </label>
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleAccept}
              disabled={!agreed || submitting || docError || docContent === null}
              data-testid="disclaimer-accept-btn"
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Kullanmaya Başla
            </Button>
          </div>
        </div>
    </div>
  );
}
